// ===========================================
// Account Settings - Helper Functions
// ===========================================

import type { PasswordStrength } from './account.types';

/**
 * Calculate password strength score and requirements
 */
export function calculatePasswordStrength(password: string): PasswordStrength {
  const requirements = [
    { met: password.length >= 8, label: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), label: 'Uppercase letter (A-Z)' },
    { met: /[a-z]/.test(password), label: 'Lowercase letter (a-z)' },
    { met: /\d/.test(password), label: 'Number (0-9)' },
    { met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password), label: 'Special character' },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  let score = (metCount / requirements.length) * 60; // Base score from requirements (0-60)

  // Bonus points for length
  if (password.length >= 12) score += 20;
  else if (password.length >= 10) score += 10;

  // Bonus for variety
  if (password.length >= 8 && metCount === 5) score += 20;

  score = Math.min(100, score);

  let label: string;
  let color: 'error' | 'warning' | 'info' | 'success';

  if (score < 40) {
    label = 'Weak';
    color = 'error';
  } else if (score < 60) {
    label = 'Fair';
    color = 'warning';
  } else if (score < 80) {
    label = 'Good';
    color = 'info';
  } else {
    label = 'Strong';
    color = 'success';
  }

  return { score, label, color, requirements };
}

/**
 * Account Settings' crumbs. The root is a bare view root, which `PageHeader` turns into the view
 * the user is IN, with the school slug (#434): it used to be chosen by the LOGIN role, so a staff
 * login on /admin/account saw "Teacher", and every root but /admin had no slug and 404'd.
 */
export function getAccountBreadcrumbs() {
  return [{ label: 'Admin', path: '/admin' }, { label: 'Account Settings' }];
}
